import { Link } from '@tanstack/react-router';
import {
  Users,
  ClipboardList,
  MessageCircleMore,
  PenTool,
  Lock,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  TooltipProvider,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/shared/components/ui/Tooltip';
import type { Signal } from '@momentum/types';
import { SignalOriginType } from '@momentum/types';

interface SignalDetailMetaBarProps {
  origin: Signal['origin'];
  installedCount: number;
  isActive: boolean;
  appliedByName?: string;
}

export function SignalDetailMetaBar({
  origin,
  installedCount,
  isActive,
  appliedByName,
}: SignalDetailMetaBarProps) {
  const { t } = useTranslation('knowledge');

  const originLabel = (() => {
    switch (origin.type) {
      case SignalOriginType.DEBRIEF:
        return t('originDebrief');
      case SignalOriginType.FEEDBACK:
        return t('originFeedback');
      case SignalOriginType.MANUAL:
        return t('originManual');
      case SignalOriginType.EXTERNAL:
        return t('originExternal');
    }
  })();

  const originIcon = (() => {
    switch (origin.type) {
      case SignalOriginType.DEBRIEF:
        return <ClipboardList className="h-3 w-3" />;
      case SignalOriginType.FEEDBACK:
        return <MessageCircleMore className="h-3 w-3" />;
      case SignalOriginType.MANUAL:
        return <PenTool className="h-3 w-3" />;
      case SignalOriginType.EXTERNAL:
        return <PenTool className="h-3 w-3" />;
    }
  })();

  const hasOriginLink =
    origin.type === SignalOriginType.DEBRIEF ||
    origin.type === SignalOriginType.FEEDBACK;

  const originLinkTo =
    origin.type === SignalOriginType.DEBRIEF
      ? '/interactions/debrief/$id'
      : origin.type === SignalOriginType.FEEDBACK
      ? '/interactions/feedback/$id'
      : undefined;

  const originInteractionId =
    origin.type === SignalOriginType.DEBRIEF ||
    origin.type === SignalOriginType.FEEDBACK
      ? origin.interactionId
      : undefined;

  return (
    <div
      className={`flex items-center gap-3 pb-6 text-xs text-muted-foreground/80 ${
        appliedByName ? 'justify-between' : 'justify-end'
      }`}
    >
      {appliedByName && (
        <span className="inline-flex items-center gap-1.5 rounded-md bg-muted px-2.5 py-1 text-[12px] text-muted-foreground">
          <Lock className="h-[11px] w-[11px] shrink-0 text-muted-foreground/60" />
          {t('detailScopeAppliedBy')(appliedByName)}
        </span>
      )}
      <div className="flex items-center gap-3">
        {hasOriginLink && originLinkTo && originInteractionId ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to={originLinkTo}
                  params={{ id: originInteractionId }}
                  className="inline-flex items-center gap-1.5 transition-colors hover:text-[#5E6AD2]"
                  onClick={(e) => e.stopPropagation()}
                >
                  {originIcon}
                  {originLabel}
                </Link>
              </TooltipTrigger>
              <TooltipContent>{t('detailViewSource')}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <span className="inline-flex items-center gap-1.5">
            {originIcon}
            {originLabel}
          </span>
        )}
        <span>&middot;</span>
        <span className="inline-flex items-center gap-1.5">
          <Users className="h-3 w-3" />
          {t('detailInstalled')(installedCount)}
        </span>
        <span>&middot;</span>
        <span
          className="inline-flex items-center gap-1.5 font-medium"
          style={{ color: isActive ? '#16a34a' : '#94a3b8' }}
        >
          <span
            className="h-1.5 w-1.5 rounded-full"
            style={{ backgroundColor: isActive ? '#16a34a' : '#94a3b8' }}
          />
          {isActive ? t('active') : t('inactive')}
        </span>
      </div>
    </div>
  );
}
